import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { agentAppAPI } from '../../services/api';

const EMPTY_FORM = { name: '', mobile: '', society: '', houseNo: '', connectionType: 'catv', note: '' };

const CONNECTION_TYPES = [
  { value: 'catv', label: 'CableTV' },
  { value: 'broadband', label: 'Broadband' },
  { value: 'both', label: 'Both' },
];

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

// Bottom-nav "Lead" tab — a field agent canvassing door-to-door logs a prospect's basic
// details on the spot, no Customer record required. Submitting saves it to the same Leads
// inbox staff triage at the top-level Leads nav item, and immediately WhatsApps the prospect
// a thank-you + a short plan catalog (see backend routes/agentApp.js POST /leads) so the
// agent doesn't have to recite pricing standing on someone's doorstep.
export default function AgentLeadPage() {
  const { setHeader } = useOutletContext();
  const [form, setForm] = useState(EMPTY_FORM);
  // false | 'save' | 'notify' — which button (if either) is mid-request, so only that
  // button shows its own "Saving…" label while both are disabled to prevent a double-submit.
  const [saving, setSaving] = useState(false);
  const [areaOptions, setAreaOptions] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { setHeader({ title: 'New Lead', showBack: false }); }, [setHeader]);
  useEffect(() => { agentAppAPI.getAreas().then(({ data }) => setAreaOptions(data.data || [])).catch(() => {}); }, []);

  const submit = async (notify) => {
    if (!form.name.trim() || !form.mobile.trim() || !form.society.trim() || !form.houseNo.trim()) {
      toast.error('Name, mobile, society and house no. are required');
      return;
    }
    setSaving(notify ? 'notify' : 'save');
    try {
      const { data } = await agentAppAPI.addLead({
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        society: form.society.trim(),
        houseNo: form.houseNo.trim(),
        connectionType: form.connectionType,
        note: form.note.trim() || undefined,
        notify,
      });
      toast.success(data.message || 'Lead saved');
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 14 }}>
      <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>
        Log a new prospect's details, then either just save it for our team to follow up, or
        also send them a WhatsApp on the spot thanking them for their interest with our plans.
      </div>

      <Field label="Name" required>
        <input className="form-control" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Prospect's name" />
      </Field>
      <Field label="Mobile Number" required>
        <input className="form-control" type="tel" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} placeholder="10-digit mobile number" />
      </Field>
      <Field label="Society / Area" required>
        <input className="form-control" list="agent-lead-area-options" value={form.society} onChange={(e) => set('society', e.target.value)} placeholder="Select or type a society/area" />
        <datalist id="agent-lead-area-options">
          {areaOptions.map((a) => <option key={a} value={a} />)}
        </datalist>
      </Field>
      <Field label="House No." required>
        <input className="form-control" value={form.houseNo} onChange={(e) => set('houseNo', e.target.value)} />
      </Field>
      <Field label="Interested In" required>
        <div style={{ display: 'flex', gap: 16 }}>
          {CONNECTION_TYPES.map((t) => (
            <label key={t.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer' }}>
              <input type="radio" name="connectionType" checked={form.connectionType === t.value} onChange={() => set('connectionType', t.value)} />
              {t.label}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Note">
        <textarea className="form-control" rows={2} value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="Anything staff should know" />
      </Field>

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button
          type="button" disabled={!!saving} onClick={() => submit(false)}
          style={{ flex: 1, background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}
        >
          {saving === 'save' ? 'Saving…' : 'Save Lead'}
        </button>
        <button
          type="button" disabled={!!saving} onClick={() => submit(true)}
          style={{ flex: 1, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}
        >
          {saving === 'notify' ? 'Sending…' : 'Save Lead & Notify'}
        </button>
      </div>
    </div>
  );
}
